<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';

const props = defineProps({
  contactId: {
    type: [String, Number],
    default: null,
  },
});

const store = useStore();
const route = useRoute();

const showDropdown = ref(false);
const searchQuery = ref('');

const allLabels = useMapGetter('labels/getLabels');
const contactLabels = useMapGetter('contactLabels/getContactLabels');

const savedLabels = computed(() => {
  const availableContactLabels = contactLabels.value(props.contactId);
  return allLabels.value.filter(({ title }) =>
    availableContactLabels.includes(title)
  );
});

const labelMenuItems = computed(() => {
  return (allLabels.value || []).map(label => ({
    label: label.title,
    value: label.id,
    color: label.color,
    isSelected: savedLabels.value.some(
      savedLabel => savedLabel.id === label.id
    ),
  }));
});

const filteredItems = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return labelMenuItems.value;
  return labelMenuItems.value.filter(i =>
    i.label.toLowerCase().includes(q)
  );
});

const selectedFilteredItems = computed(() =>
  filteredItems.value.filter(i => i.isSelected)
);
const unselectedFilteredItems = computed(() =>
  filteredItems.value.filter(i => !i.isSelected)
);

const fetchLabels = async contactId => {
  if (!contactId) return;
  store.dispatch('contactLabels/get', contactId);
};

const handleLabelAction = async item => {
  try {
    const currentLabels = savedLabels.value.map(l => l.title);
    const target = allLabels.value.find(l => l.id === item.value);
    if (!target) return;
    const updatedLabels = currentLabels.includes(target.title)
      ? currentLabels.filter(t => t !== target.title)
      : [...currentLabels, target.title];
    await store.dispatch('contactLabels/update', {
      contactId: props.contactId,
      labels: updatedLabels,
    });
    // SZ_CONTACT_LABELS_MULTI_20260526: NÃO fechar o dropdown ao selecionar —
    // multi-select continua aberto até clicar fora.
  } catch (error) {
    // error
  }
};

const handleRemoveLabel = label =>
  handleLabelAction({ value: label.id });

watch(
  () => props.contactId,
  (newVal, oldVal) => {
    if (newVal !== oldVal) fetchLabels(newVal);
  }
);
watch(showDropdown, isOpen => {
  if (isOpen) searchQuery.value = '';
});
onMounted(() => {
  if (route.params.contactId) fetchLabels(route.params.contactId);
});

const closeDropdown = () => { showDropdown.value = false; };
</script>

<template>
  <div class="sz-cl-row" v-on-click-outside="closeDropdown">
    <div class="sz-cl-add-wrap">
      <button
        type="button"
        class="sz-cl-add-btn"
        :aria-expanded="showDropdown"
        @click="showDropdown = !showDropdown"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="12" y1="5" x2="12" y2="19"/>
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        <span>Etiquetas</span>
      </button>

      <div v-if="showDropdown" class="sz-cl-dropdown">
        <!-- Search -->
        <div class="sz-cl-search">
          <svg class="sz-cl-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="7"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            class="sz-cl-search-input"
            placeholder="Pesquisar etiquetas"
          />
        </div>

        <div class="sz-cl-list">
          <!-- Selecionadas -->
          <template v-if="selectedFilteredItems.length">
            <div class="sz-cl-section">Selecionadas</div>
            <button
              v-for="item in selectedFilteredItems"
              :key="'s-' + item.value"
              type="button"
              class="sz-cl-dd-item"
              @click="handleLabelAction(item)"
            >
              <span class="sz-cl-cb sz-cl-cb--checked" aria-hidden="true">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </span>
              <span class="sz-cl-dd-dot" :style="{ background: item.color }" />
              <span class="sz-cl-dd-text" :style="{ color: item.color }">{{ item.label }}</span>
            </button>
          </template>

          <div
            v-if="selectedFilteredItems.length && unselectedFilteredItems.length"
            class="sz-cl-divider"
            role="presentation"
          />

          <!-- Não selecionadas -->
          <button
            v-for="item in unselectedFilteredItems"
            :key="'u-' + item.value"
            type="button"
            class="sz-cl-dd-item"
            @click="handleLabelAction(item)"
          >
            <span class="sz-cl-cb" aria-hidden="true" />
            <span class="sz-cl-dd-dot" :style="{ background: item.color }" />
            <span class="sz-cl-dd-text" :style="{ color: item.color }">{{ item.label }}</span>
          </button>

          <div v-if="!filteredItems.length" class="sz-cl-dd-empty">
            Sem etiquetas
          </div>
        </div>
      </div>
    </div>

    <!-- SZ_CONTACT_LABELS_PILL_20260526: chip no mesmo design das pills da lista
         de chats (CardLabels.vue) — bg = color+1A (10% opacidade), height 16px,
         dot 9px, font 11px. X mantido pra remover. -->
    <span
      v-for="label in savedLabels"
      :key="label.id"
      class="sz-cl-chip"
      :style="{
        background: label.color + '1A',
        color: label.color,
      }"
    >
      <span class="sz-cl-chip-dot" :style="{ background: label.color }" />
      <span class="sz-cl-chip-text">{{ label.title }}</span>
      <button
        type="button"
        class="sz-cl-chip-x"
        :aria-label="`Remover ${label.title}`"
        @click="handleRemoveLabel(label)"
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </span>
  </div>
</template>

<style scoped>
.sz-cl-row {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  align-content: center;
  gap: 4px;
  width: 100%;
  min-height: 24px;
  font-family: 'Inter', system-ui, sans-serif;
}

.sz-cl-add-wrap { position: relative; }
.sz-cl-add-btn {
  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0 8px;
  gap: 8px;
  height: 24px;
  background: #DEDEDE;
  border-radius: 999px;
  border: 0;
  cursor: pointer;
  color: #000000;
  font-family: inherit;
  font-weight: 500;
  font-size: 12px;
  line-height: 20px;
  transition: background 0.12s;
}
.sz-cl-add-btn:hover { background: #D0D0D0; }
.dark .sz-cl-add-btn { background: #3D3F43; color: #FFFFFF; }
.dark .sz-cl-add-btn:hover { background: #4A4D52; }

/* Chip = mesma pill da lista de chats (CardLabels.vue) */
.sz-cl-chip {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 4px 2px 4px;
  height: 16px;
  border-radius: 29px;
  font-family: inherit;
  font-weight: 500;
  font-size: 11px;
  line-height: 100%;
  white-space: nowrap;
  box-sizing: border-box;
  max-width: 200px;
}
.sz-cl-chip-dot {
  width: 9px;
  height: 9px;
  min-width: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}
.sz-cl-chip-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 2px;
}
.sz-cl-chip-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  padding: 0;
  margin-left: 1px;
  background: transparent;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  color: currentColor;
  opacity: 0.65;
  flex-shrink: 0;
  transition: opacity 0.12s, background 0.12s;
}
.sz-cl-chip-x:hover { opacity: 1; background: rgba(0, 0, 0, 0.08); }
.dark .sz-cl-chip-x:hover { background: rgba(255, 255, 255, 0.12); }

/* === Dropdown === */
.sz-cl-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 50;
  width: 280px;
  max-height: 380px;
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.dark .sz-cl-dropdown {
  background: #1D1E21;
  border-color: #3D3F43;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
}

/* Search */
.sz-cl-search {
  position: relative;
  display: flex;
  align-items: center;
}
.sz-cl-search-icon {
  position: absolute;
  left: 10px;
  color: #9CA3AF;
  pointer-events: none;
}
.sz-cl-search-input {
  width: 100%;
  height: 32px;
  padding: 0 10px 0 30px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  background: #FFFFFF;
  font-family: inherit;
  font-size: 13px;
  color: #1F2937;
  outline: none;
  box-sizing: border-box;
}
.sz-cl-search-input::placeholder { color: #9CA3AF; }
.sz-cl-search-input:focus {
  border-color: #388E3C;
  box-shadow: 0 0 0 2px rgba(56, 142, 60, 0.12);
}
.dark .sz-cl-search-input {
  background: #1D1E21;
  border-color: #3D3F43;
  color: #E5E7EB;
}
.dark .sz-cl-search-input::placeholder { color: #6B7280; }

/* List */
.sz-cl-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
  max-height: 320px;
  margin: 0 -4px;
  padding: 0 4px;
}

.sz-cl-section {
  font-size: 12px;
  font-weight: 600;
  color: #1F2937;
  padding: 4px 6px 2px;
  letter-spacing: 0.01em;
}
.dark .sz-cl-section { color: #E5E7EB; }

.sz-cl-divider {
  height: 1px;
  background: #E5E7EB;
  margin: 6px 0;
}
.dark .sz-cl-divider { background: #2A2C30; }

.sz-cl-dd-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 6px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  transition: background 0.1s ease;
}
.sz-cl-dd-item:hover { background: #F3F4F6; }
.dark .sz-cl-dd-item:hover { background: #242528; }

/* Checkbox */
.sz-cl-cb {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1.5px solid #D1D5DB;
  background: transparent;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  transition: background 0.12s, border-color 0.12s;
}
.sz-cl-cb--checked {
  background: #388E3C;
  border-color: #388E3C;
}
.dark .sz-cl-cb { border-color: #3D3F43; }
.dark .sz-cl-cb--checked { background: #388E3C; border-color: #388E3C; }

.sz-cl-dd-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.sz-cl-dd-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sz-cl-dd-empty {
  padding: 14px 10px;
  text-align: center;
  color: #9CA3AF;
  font-size: 13px;
}
</style>